import React, { useState, useCallback, useEffect, useRef } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { useTranslation } from 'localization';
import type { SphereStatusPoint } from 'types/global';
import type { VelocityBucketKey } from 'types/sphereMetrics';
import type { PermissionList } from '../../../../../types/sharedTypes';
import ButtonAtom from '../../../atoms/Button/ButtonAtom';
import { VelocityIndicator } from '../../../atoms/VelocityIndicator';
import { useApiClientsOrNull } from '../../../../context/ApiClientsContext';
import { useSchemaType } from '../../../../hooks/useSchemaType';
import { useStatusPopoverContext } from './StatusPopoverContext';
import StatusFactorPanel, { FactorData, StatusValue as PanelStatusValue, PanelMode } from './StatusFactorPanel';
import {
  buildShowOnCardsLookup,
  isFactorOnCard,
  resolveFactorDisplayName,
} from '../../../../utils/factorDisplay';
import './StatusPopoverMolecule.scss';

/** Velocity response with compliance data from API */
interface VelocityWithCompliance {
  baseVelocity?: {
    score: number;
    direction: 'improving' | 'stable' | 'declining';
    bucket: VelocityBucketKey;
  };
  velocity?: {
    score: number;
    direction: 'improving' | 'stable' | 'declining';
    bucket: VelocityBucketKey;
  };
  compliance?: {
    degraded: boolean;
    overdueFactors: number;
    maxDaysOverdue: number;
    penaltyApplied: number;
  };
}

export interface StatusPopoverContentProps {
  sphereId: string;
  sphereName: string;
  schemaType?: string;
  status?: SphereStatusPoint;
  isDeploying: boolean;
  deployError: string | null;
  onDeployFactors: () => void;
  isRefreshing: boolean;
  onRefresh: () => void;
  onClose: () => void;
  apiClients: ReturnType<typeof useApiClientsOrNull>;
  // Panel props
  selectedFactor: FactorData | null;
  panelMode: PanelMode;
  panelLoading: boolean;
  onFactorClick: (factor: FactorData, mode: PanelMode) => void;
  onPanelClose: () => void;
  onCheckInSubmit: (statusValue: PanelStatusValue, comment?: string) => void;
  // Permission state - null = not checked yet, false = no edit, true = has edit
  hasEditPermission: boolean | null;
  // Configure callback - opens SphereEditor to Status Factors tab
  onConfigureFactors?: () => void;
  // Velocity with compliance data (fetched separately)
  velocityData?: VelocityWithCompliance | null;
}

/**
 * Inner component that renders popover content.
 * This is a separate component so hooks are called at the top level.
 * Only mounts when OverlayPanel renders its children.
 */
export const StatusPopoverContent: React.FC<StatusPopoverContentProps> = ({
  sphereId,
  sphereName,
  schemaType,
  status,
  isDeploying,
  deployError,
  onDeployFactors,
  isRefreshing,
  onRefresh,
  onClose,
  apiClients,
  selectedFactor,
  panelMode,
  panelLoading,
  onFactorClick,
  onPanelClose,
  onCheckInSubmit,
  hasEditPermission,
  onConfigureFactors,
  velocityData,
}) => {
  const { t } = useTranslation();

  // Find the schema definition for this sphere's type
  const { schema: schemaDefinition } = useSchemaType(schemaType ?? null);

  const statusFactorsConfig = schemaDefinition?.statusFactors;

  // Helper to build a detailed description of a schema factor
  const getSchemaFactorDetails = (factor: any): { name: string; description: string } => {
    const config = factor.config || {};
    const factorType = factor.factorType;

    if (factorType === 'related_spheres') {
      // Build a human-readable description of what spheres are monitored
      const monitoringRules = config.monitoringRules || {};
      const perspectiveRel = monitoringRules.perspectiveRelationship;
      const sphereFilters = monitoringRules.sphereFilters?.propertyFilters || [];
      const aggregationMethod = config.aggregationMethod || 'worst';
      const emptyBehavior = config.emptyBehavior;

      // Determine relationship direction
      let relationshipDesc = t('statusPopover.factorDetails.childSpheres');
      if (perspectiveRel) {
        const perspectiveName = perspectiveRel.perspectiveSystemName?.replace('center_', '').replaceAll('_', ' ');
        if (perspectiveRel.direction === 'outbound') {
          relationshipDesc = t('statusPopover.factorDetails.spheresWhereThisIs', { perspective: perspectiveName });
        } else {
          relationshipDesc = t('statusPopover.factorDetails.spheresAssignedAs', { perspective: perspectiveName });
        }
      }

      // Build filter description
      let filterDesc = '';
      if (sphereFilters.length > 0) {
        const filterParts = sphereFilters.map((f: any) => {
          const opMap: Record<string, string> = {
            'gte': '>=', 'gt': '>', 'lte': '<=', 'lt': '<', 'equals': '=', 'ne': '!='
          };
          return `${f.property} ${opMap[f.operator] || f.operator} ${f.value}`;
        });
        filterDesc = t('statusPopover.factorDetails.filteredBy', { filters: filterParts.join(', ') });
      }

      // Build aggregation description
      const aggDesc = t(`statusPopover.factorDetails.aggregation.${aggregationMethod}`) || aggregationMethod;

      // Build empty behavior description
      let emptyDesc = '';
      if (emptyBehavior === 'green') {
        emptyDesc = t('statusPopover.factorDetails.emptyBehavior.green');
      } else if (emptyBehavior === 'yellow') {
        emptyDesc = t('statusPopover.factorDetails.emptyBehavior.yellow');
      } else if (emptyBehavior === 'red') {
        emptyDesc = t('statusPopover.factorDetails.emptyBehavior.red');
      }

      const descriptionParts = [relationshipDesc, filterDesc, aggDesc, emptyDesc].filter(Boolean);

      return {
        name: t('statusPopover.factorNames.related_spheres'),
        description: descriptionParts.join('. ')
      };
    }

    // Fallback for other factor types
    const nameKey = `statusPopover.factorNames.${factorType}`;
    const descKey = `statusPopover.factorDescriptions.${factorType}`;
    return {
      name: t(nameKey) === nameKey ? factorType.replaceAll('_', ' ') : t(nameKey),
      description: t(descKey) === descKey ? t('statusPopover.factorDescriptions.custom') : t(descKey)
    };
  };

  // Helper to get user-friendly factor descriptions (for deployed factors)
  const getFactorDescription = (factorType: string): string => {
    const key = `statusPopover.factorDescriptions.${factorType}`;
    const translated = t(key);
    return translated === key ? t('statusPopover.factorDescriptions.custom') : translated;
  };

  // Localized fallback label for a factor whose owner never set a name.
  const getFactorTypeLabel = (factorType: string): string => {
    const key = `statusPopover.factorNames.${factorType}`;
    const translated = t(key);
    return translated === key ? factorType.replaceAll('_', ' ') : translated;
  };

  // Helper to get status value label with fallback
  const getStatusLabel = (statusVal: string): string => {
    const key = `statusPopover.statusValues.${statusVal}`;
    const translated = t(key);
    return translated === key ? statusVal : translated;
  };

  // Helper to get the icon class for velocity bucket
  const getVelocityIconClass = (bucket: string): string => {
    const iconMap: Record<string, string> = {
      'rapidly_improving': 'pi-angle-double-up',
      'improving': 'pi-angle-up',
      'stable': 'pi-minus',
      'declining': 'pi-angle-down',
      'rapidly_declining': 'pi-angle-double-down',
    };
    return iconMap[bucket] || 'pi-question';
  };

  // Check if schema has status factors defined
  const schemaHasStatusFactors = statusFactorsConfig?.enabled === true &&
    (statusFactorsConfig?.factors?.length || 0) > 0;

  // Get the number of factors defined in schema
  const schemaFactorCount = statusFactorsConfig?.factors?.length || 0;

  // Get schema factors for display
  const schemaFactors = statusFactorsConfig?.factors || [];

  // No status point configured at all
  if (!status) {
    // Schema has status factors defined - show deploy option
    if (schemaHasStatusFactors) {
      return (
        <>
          <div className="status-popover-header">
            <h3>{sphereName}</h3>
          </div>
          <div className="status-popover-content">
            <div className="status-popover-deploy-state">
              <div className="deploy-state-icon">🚀</div>
              <h4>{t('statusPopover.schemaFactorsAvailable.title')}</h4>
              <p>
                {t('statusPopover.schemaFactorsAvailable.description', { count: schemaFactorCount })}
              </p>
              <ul className="status-factor-list status-factor-list--preview">
                {schemaFactors.map((factor: any, index: number) => {
                  const details = getSchemaFactorDetails(factor);
                  // Use factorType + index for stable key since schema factors don't have IDs
                  const factorKey = `${factor.factorType}-${index}`;
                  return (
                    <li key={factorKey} className="status-factor-item">
                      <div className="status-factor-item__header">
                        <span className="status-factor-item__name">
                          {details.name}
                        </span>
                        <span className="status-factor-item__weight">
                          {t('statusPopover.weight')}: {factor.weight}
                        </span>
                      </div>
                      <div className="status-factor-item__description">
                        {details.description}
                      </div>
                    </li>
                  );
                })}
              </ul>
              {deployError && (
                <div className="deploy-error">
                  <small>{deployError}</small>
                </div>
              )}
              <ButtonAtom
                label={isDeploying
                  ? t('statusPopover.schemaFactorsAvailable.deployingButton')
                  : t('statusPopover.schemaFactorsAvailable.deployButton')
                }
                icon={isDeploying ? 'pi pi-spin pi-spinner' : 'pi pi-bolt'}
                onClick={onDeployFactors}
                disabled={isDeploying || !apiClients}
                variant="primary"
                shape="pill"
              />
              <p className="deploy-state-note">
                <small>{t('statusPopover.schemaFactorsAvailable.note')}</small>
              </p>
            </div>
          </div>
        </>
      );
    }

    // No schema factors - show generic "no tracking" state
    return (
      <>
        <div className="status-popover-header">
          <h3>{sphereName}</h3>
        </div>
        <div className="status-popover-content">
          <div className="status-popover-empty-state">
            <div className="empty-state-icon">📊</div>
            <h4>{t('statusPopover.noStatusTracking.title')}</h4>
            <p>
              {t('statusPopover.noStatusTracking.description')}
            </p>
            <ButtonAtom
              label={t('statusPopover.noStatusTracking.configureButton')}
              icon="pi pi-cog"
              onClick={onConfigureFactors}
              disabled={!onConfigureFactors}
              variant="secondary"
              shape="pill"
            />
            {!onConfigureFactors && (
              <p className="empty-state-note">
                <small>{t('statusPopover.noStatusTracking.comingSoon')}</small>
              </p>
            )}
          </div>
        </div>
      </>
    );
  }

  // Status point exists - extract data
  const { currentValue, computedAt, factorDefinitions } = status;
  const factorCount = factorDefinitions?.length || 0;
  const deployedFactorCount = factorDefinitions?.filter(f => f.deployed).length || 0;
  // Also check currentValue.factors - the API may return computed factors without factorDefinitions
  const currentValueFactorCount = currentValue?.factors?.length || 0;
  const hasFactors = factorCount > 0 || currentValueFactorCount > 0;
  const hasDeployedFactors = deployedFactorCount > 0 || currentValueFactorCount > 0;
  const statusValue = currentValue?.status || 'unknown';
  const hasManualFactors = factorDefinitions?.some(f => f.deployed && f.factorType === 'manual') ||
    currentValue?.factors?.some(f => f.factorType === 'manual') || false;
  const isStatusComputed = statusValue !== 'unknown' || hasManualFactors;
  const statusLabel = getStatusLabel(statusValue);
  const velocity = currentValue?.velocity;

  // Render status content based on state
  const renderStatusContent = () => {
    if (!hasFactors) {
      // No factors deployed - check if schema has factors to deploy
      if (schemaHasStatusFactors) {
        return (
          <div className="status-popover-deploy-state">
            <div className="deploy-state-icon">🚀</div>
            <h4>{t('statusPopover.schemaFactorsAvailable.title')}</h4>
            <p>
              {t('statusPopover.schemaFactorsAvailable.description', { count: schemaFactorCount })}
            </p>
            <ul className="status-factor-list status-factor-list--preview">
              {schemaFactors.map((factor: any, index: number) => {
                const details = getSchemaFactorDetails(factor);
                // Use factorType + index for stable key since schema factors don't have IDs
                const factorKey = `${factor.factorType}-${index}`;
                return (
                  <li key={factorKey} className="status-factor-item">
                    <div className="status-factor-item__header">
                      <span className="status-factor-item__name">
                        {details.name}
                      </span>
                      <span className="status-factor-item__weight">
                        {t('statusPopover.weight')}: {factor.weight}
                      </span>
                    </div>
                    <div className="status-factor-item__description">
                      {details.description}
                    </div>
                  </li>
                );
              })}
            </ul>
            {deployError && (
              <div className="deploy-error">
                <small>{deployError}</small>
              </div>
            )}
            <ButtonAtom
              label={isDeploying
                ? t('statusPopover.schemaFactorsAvailable.deployingButton')
                : t('statusPopover.schemaFactorsAvailable.deployButton')
              }
              icon={isDeploying ? 'pi pi-spin pi-spinner' : 'pi pi-bolt'}
              onClick={onDeployFactors}
              disabled={isDeploying || !apiClients}
              variant="primary"
              shape="pill"
            />
            <p className="deploy-state-note">
              <small>{t('statusPopover.schemaFactorsAvailable.note')}</small>
            </p>
          </div>
        );
      }

      // No factors configured and no schema factors available
      return (
        <div className="status-popover-empty-state">
          <div className="empty-state-icon">📊</div>
          <h4>{t('statusPopover.noFactors.title')}</h4>
          <p>
            {t('statusPopover.noFactors.description')}
          </p>
          <ButtonAtom
            label={t('statusPopover.noFactors.configureButton')}
            icon="pi pi-cog"
            onClick={onConfigureFactors}
            disabled={!onConfigureFactors}
            variant="secondary"
            shape="pill"
          />
          {!onConfigureFactors && (
            <p className="empty-state-note">
              <small>{t('statusPopover.noFactors.comingSoon')}</small>
            </p>
          )}
        </div>
      );
    }

    if (!hasDeployedFactors) {
      // Has factors but none deployed
      return (
        <div className="status-popover-draft-state">
          <div className="draft-state-icon">📝</div>
          <h4>{t('statusPopover.notDeployed.title')}</h4>
          <p>
            {t('statusPopover.notDeployed.description', { count: factorCount })}
          </p>
          <ul className="status-factor-list">
            {/* EAINT-750: filter opt-outs on the pre-deploy preview too, so
                what the owner sees here matches what will render post-deploy. */}
            {factorDefinitions?.filter(f => f.showOnCards !== false).map(factor => (
              <li key={factor.id} className="status-factor-item">
                <div className="status-factor-item__header">
                  <span className="status-factor-item__name">
                    {resolveFactorDisplayName(factor, getFactorTypeLabel(factor.factorType))}
                  </span>
                  <span className="status-factor-item__weight">
                    {t('statusPopover.weight')}: {factor.weight}
                  </span>
                </div>
                <div className="status-factor-item__description">
                  {getFactorDescription(factor.factorType)}
                </div>
              </li>
            ))}
          </ul>
          <p className="draft-state-note">
            {t('statusPopover.notDeployed.note')}
          </p>
          <ButtonAtom
            label={t('statusPopover.notDeployed.deployButton')}
            icon="pi pi-bolt"
            disabled
            variant="secondary"
            shape="pill"
          />
          <p className="empty-state-note">
            <small>{t('statusPopover.notDeployed.comingSoon')}</small>
          </p>
        </div>
      );
    }

    if (isStatusComputed) {
      // Status has been computed - show factor breakdown
      // EAINT-750: hide factors the sphere owner opted out of showing on cards.
      // Shared helper cross-references factorDefinitions (live vertex property)
      // so a stale status-point summary doesn't keep opted-out factors visible.
      const definitionsById = buildShowOnCardsLookup(factorDefinitions);
      const displayFactors = currentValue.factors?.length > 0
        ? currentValue.factors.filter(f => isFactorOnCard(f, definitionsById))
        : factorDefinitions?.filter(f => f.deployed && isFactorOnCard(f, definitionsById));

      console.log('[StatusPopoverContent] Computed state - factors:', {
        currentValueFactors: currentValue.factors,
        factorDefinitions,
        displayFactors
      });

      return (
        <div className="status-popover-computed-state">
          <div className="status-popover-computed-state__header">
            <h4>{t('statusPopover.computed.title')}</h4>
            {hasEditPermission === true && onConfigureFactors && (
              <button
                type="button"
                className="status-popover-configure-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onConfigureFactors();
                }}
                title={t('statusPopover.computed.configureTooltip')}
                aria-label={t('statusPopover.computed.configureTooltip')}
              >
                <i className="pi pi-cog" />
              </button>
            )}
          </div>
          <p>
            {t('statusPopover.computed.description')}
          </p>

          <ul className="status-factor-list">
            {displayFactors?.map(factor => {
              const factorStatus = factor.status || factor.currentStatus || 'unknown';
              const factorStatusLabel = getStatusLabel(factorStatus);
              const statusClass = `status-factor-item__status--${factorStatus.replaceAll('_', '-')}`;
              const factorMode: 'active' | 'observing' | 'paused' = factor.mode ?? 'active';
              const isNonActiveMode = factorMode !== 'active';
              const modeLabel = t(`statusFactors.mode.${factorMode}`, factorMode);
              // Get factor velocity from velocityScore/velocityBucket
              const hasVelocity = factor.velocityScore !== undefined && factor.velocityBucket;

              // Build FactorData for panel
              const factorData: FactorData = {
                id: factor.id,
                factorType: factor.factorType,
                // Shared resolver reads summary.name AND metadata.name so the
                // same call works whether `factor` is a summary or a factorDefinition.
                name: resolveFactorDisplayName(
                  factor as { name?: string; metadata?: { name?: string } },
                  getFactorTypeLabel(factor.factorType),
                ),
                description: getFactorDescription(factor.factorType),
                weight: factor.weight,
                status: factorStatus,
                statusLabel: factorStatusLabel,
                velocityScore: factor.velocityScore,
                velocityBucket: factor.velocityBucket as VelocityBucketKey,
                commentRequired: factor.commentRequired,
                minCommentLength: factor.minCommentLength,
                scheduleBroken: (factor as { metadata?: { scheduleBroken?: { code: string; message: string; detectedAt: string } } })
                  .metadata?.scheduleBroken,
              };

              // Check if this is a manual factor that can have check-ins
              const isManual = factor.factorType === 'manual';
              const showCheckInButton = isManual && hasEditPermission === true;
              const modeClass = isNonActiveMode ? ` status-factor-item--${factorMode}` : '';

              return (
                <li
                  key={factor.id}
                  className={`status-factor-item status-factor-item--clickable${modeClass}`}
                >
                  <div className="status-factor-item__header">
                    {/* Real button as the row's primary action — its stretched ::after
                        makes the whole card clickable without nesting the check-in
                        button inside another interactive element. */}
                    <button
                      type="button"
                      className="status-factor-item__trigger"
                      onClick={() => onFactorClick(factorData, 'history')}
                    >
                      <span className="status-factor-item__name">
                        {factorData.name}
                      </span>
                    </button>
                    {!isNonActiveMode && (
                      <span className="status-factor-item__weight">
                        {t('statusPopover.weight')}: {factor.weight}
                      </span>
                    )}
                  </div>
                  <div className="status-factor-item__status-row">
                    {/* Show velocity indicator before status chip, but only if we have meaningful velocity data */}
                    {hasVelocity && factorStatus !== 'unknown' && (
                      <VelocityIndicator
                        score={factor.velocityScore ?? 0}
                        bucket={factor.velocityBucket as VelocityBucketKey}
                        size="small"
                      />
                    )}
                    <div className={`status-factor-item__status ${statusClass}`}>
                      {factorStatusLabel}
                    </div>
                    {isNonActiveMode && (
                      <div className={`status-factor-item__mode-badge status-factor-item__mode-badge--${factorMode}`}>
                        {modeLabel}
                      </div>
                    )}
                    {showCheckInButton && (
                      <button
                        type="button"
                        className="status-factor-checkin-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onFactorClick(factorData, 'checkin');
                        }}
                        title={t('statusPopover.checkIn.buttonTooltip', 'Submit a check-in for this factor')}
                      >
                        <i className="pi pi-pencil" />
                        {t('statusPopover.checkIn.button', 'Check In')}
                      </button>
                    )}
                    <i className="pi pi-chevron-right status-factor-item__arrow" />
                  </div>
                  <div className="status-factor-item__description">
                    {factorData.description}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      );
    }

    // Has deployed factors but status not computed yet
    return (
      <div className="status-popover-draft-state">
        <div className="draft-state-icon">⏳</div>
        <h4>{t('statusPopover.waitingComputation.title')}</h4>
        <p>
          {t('statusPopover.waitingComputation.description')}
        </p>
        <ul className="status-factor-list">
          {/* EAINT-750: filter opt-outs. See computed-state block above. */}
          {factorDefinitions?.filter(f => f.deployed && f.showOnCards !== false).map(factor => (
            <li key={factor.id} className="status-factor-item">
              <div className="status-factor-item__header">
                <span className="status-factor-item__name">
                  {resolveFactorDisplayName(factor, getFactorTypeLabel(factor.factorType))}
                </span>
                <span className="status-factor-item__weight">
                  {t('statusPopover.weight')}: {factor.weight}
                </span>
              </div>
              <div className="status-factor-item__description">
                {getFactorDescription(factor.factorType)}
              </div>
            </li>
          ))}
        </ul>
        <p className="draft-state-note">
          {t('statusPopover.waitingComputation.note')}
        </p>
      </div>
    );
  };

  // Prevent clicks and keyboard events inside popover content from closing the popover
  const handleContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleContentKeyDown = (e: React.KeyboardEvent) => {
    // Stop keyboard events from bubbling up to prevent popover from closing
    e.stopPropagation();
  };

  return (
    <div className="status-popover-container" onClick={handleContentClick} onKeyDown={handleContentKeyDown}>
      {/* Main content area */}
      <div className="status-popover-main">
        <div className="status-popover-header">
          <h3>{sphereName}</h3>
          <button
            className="status-popover-close-btn"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            title={t('common.close')}
            aria-label={t('common.close')}
          >
            <i className="pi pi-times" />
          </button>
          <div className="status-popover-header__status-row">
            <span className={`status-badge-label status-badge-label--${statusValue.replaceAll('_', '-')}`}>
              {t('statusPopover.statusLabel')}: {statusLabel}
            </span>
            {/* Use velocityData if available (includes compliance), otherwise fall back to status velocity */}
            {(() => {
              const effectiveVelocity = velocityData?.velocity || velocity;
              const compliance = velocityData?.compliance;
              const isComplianceDegraded = compliance?.degraded;
              const velocityBucket = effectiveVelocity?.bucket || 'unknown';

              return (
                <>
                  <span className={`status-badge-label status-badge-label--velocity-${velocityBucket} ${isComplianceDegraded ? 'status-badge-label--compliance-degraded' : ''}`}>
                    {t('velocity.label')}:{' '}
                    {effectiveVelocity ? (
                      <>
                        <i className={`pi ${getVelocityIconClass(effectiveVelocity.bucket)}`} />
                        {t(`velocity.buckets.${effectiveVelocity.bucket}`)}
                      </>
                    ) : (
                      t('velocity.unknown')
                    )}
                  </span>
                  {isComplianceDegraded && compliance && (
                    <span
                      className="status-badge-label status-badge-label--compliance-warning"
                      title={t('velocity.compliance.tooltip', {
                        overdueFactors: compliance.overdueFactors,
                        maxDaysOverdue: compliance.maxDaysOverdue,
                        penalty: compliance.penaltyApplied
                      })}
                    >
                      <i className="pi pi-exclamation-triangle" />
                      {t('velocity.compliance.overdue', { days: compliance.maxDaysOverdue })}
                    </span>
                  )}
                </>
              );
            })()}
            <button
              className={`status-popover-refresh-btn ${isRefreshing ? 'status-popover-refresh-btn--loading' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onRefresh();
              }}
              disabled={isRefreshing || !apiClients}
              title={t('statusPopover.refresh.tooltip')}
              aria-label={t('statusPopover.refresh.tooltip')}
            >
              <svg
                className="status-popover-refresh-btn__icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                <path d="M21 3v5h-5" />
              </svg>
            </button>
          </div>
        </div>
        <div className="status-popover-content">
          {renderStatusContent()}
        </div>
        <div className="status-popover-footer">
          <small>{t('statusPopover.lastComputed')}: {new Date(computedAt).toLocaleString()}</small>
        </div>
      </div>

      {/* Slide-out Factor Panel */}
      {selectedFactor && (
        <StatusFactorPanel
          factor={selectedFactor}
          sphereId={sphereId}
          hasEditPermission={hasEditPermission === true}
          loading={panelLoading}
          onClose={onPanelClose}
          onCheckInSubmit={onCheckInSubmit}
          apiClients={apiClients}
          mode={panelMode}
        />
      )}
    </div>
  );
};

interface StatusPopoverProps {
  sphereId: string;
  sphereName: string;
  schemaType?: string;
  status?: SphereStatusPoint;
  onStatusDeployed?: () => void;
  onStatusRefreshed?: (newStatus: SphereStatusPoint) => void;
  /** Callback when user wants to configure status factors - opens SphereEditor to Status Factors tab */
  onConfigureFactors?: (sphereId: string) => void;
  /** Function to fetch permission list - passed from parent for edit permission checks */
  fetchPermissionList?: (
    sphereId: string,
    nodeLabel: string,
    permissions?: string[]
  ) => Promise<PermissionList | null>;
}

/**
 * StatusPopoverMolecule - Displays status information when user clicks on status indicator
 *
 * Uses an inner StatusPopoverContent component that only mounts when the
 * OverlayPanel is shown, so Redux selectors and schema lookups are lazy.
 * The isVisible state ensures content is only rendered when popover is open.
 */
const StatusPopoverMolecule = React.forwardRef<OverlayPanel, StatusPopoverProps>(
  ({ sphereId, sphereName, schemaType, status, onStatusDeployed, onStatusRefreshed, onConfigureFactors, fetchPermissionList }, ref) => {
    const apiClients = useApiClientsOrNull();
    const [isDeploying, setIsDeploying] = useState(false);
    const [deployError, setDeployError] = useState<string | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    // Local state to hold refreshed status - falls back to prop if not refreshed
    const [localStatus, setLocalStatus] = useState<SphereStatusPoint | undefined>(undefined);
    const { t } = useTranslation();

    // Permission state - null = not checked, false = no edit, true = has edit
    const [hasEditPermission, setHasEditPermission] = useState<boolean | null>(null);

    // Slide-out panel state
    const [selectedFactor, setSelectedFactor] = useState<FactorData | null>(null);
    const [panelMode, setPanelMode] = useState<PanelMode>('history');
    const [panelLoading, setPanelLoading] = useState(false);

    // Velocity with compliance data (fetched separately from velocity endpoint)
    const [velocityData, setVelocityData] = useState<VelocityWithCompliance | null>(null);

    // Use local status if available (after refresh), otherwise use prop
    const effectiveStatus = localStatus || status;

    // Reset local status, permission, and velocity when sphere changes
    useEffect(() => {
      setLocalStatus(undefined);
      setHasEditPermission(null);
      setVelocityData(null);
    }, [sphereId, status]);

    // Check edit permission when popover becomes visible
    useEffect(() => {
      if (isVisible && fetchPermissionList && hasEditPermission === null) {
        (async () => {
          try {
            const permissionList = await fetchPermissionList(sphereId, sphereName, ['edit']);
            const canEdit = permissionList?.permissions?.edit ?? false;
            setHasEditPermission(canEdit);
          } catch (error) {
            console.error('[StatusPopover] Failed to check permissions:', error);
            setHasEditPermission(false);
          }
        })();
      }
    }, [isVisible, fetchPermissionList, sphereId, sphereName, hasEditPermission]);

    // Fetch velocity with compliance data when popover becomes visible
    useEffect(() => {
      if (isVisible && apiClients?.graphClient && sphereId && !velocityData) {
        (async () => {
          try {
            console.log('[StatusPopover] Fetching velocity with compliance for sphere:', sphereId);
            const data = await apiClients.graphClient.get(`/sphere/${sphereId}/velocity`);
            console.log('[StatusPopover] Velocity response:', data);
            setVelocityData(data);
          } catch (error) {
            console.error('[StatusPopover] Failed to fetch velocity:', error);
            // Non-critical - we'll fall back to status velocity
          }
        })();
      }
    }, [isVisible, apiClients, sphereId, velocityData]);

    // Context to manage single-open behavior (may be null if provider not present)
    const popoverContext = useStatusPopoverContext();

    // Create internal ref if external ref not provided, for context registration
    const internalRef = useRef<OverlayPanel>(null);
    const overlayRef = (ref as React.RefObject<OverlayPanel>) || internalRef;

    // Register/unregister with context for single-open behavior
    useEffect(() => {
      if (popoverContext && overlayRef) {
        popoverContext.registerPopover(sphereId, overlayRef);
        return () => {
          popoverContext.unregisterPopover(sphereId);
        };
      }
    }, [sphereId, popoverContext, overlayRef]);

    // Deploy status factors from schema
    const handleDeployFactors = useCallback(async () => {
      if (!apiClients?.graphClient || !sphereId) return;

      setIsDeploying(true);
      setDeployError(null);

      try {
        await apiClients.graphClient.post(`/sphere/${sphereId}/status/deploy-factors`, {
          recalculate: true
        });
        onStatusDeployed?.();
      } catch (err: any) {
        console.error('Failed to deploy status factors:', err);
        setDeployError(err.message || t('statusPopover.deployError'));
      } finally {
        setIsDeploying(false);
      }
    }, [apiClients, sphereId, onStatusDeployed, t]);

    // Refresh/recalculate status manually
    const handleRefresh = useCallback(async () => {
      if (!apiClients?.graphClient || !sphereId) return;

      setIsRefreshing(true);

      try {
        console.log('[StatusPopover] Recalculating status for sphere:', sphereId);
        // Note: graphClient.post returns data directly (not { data: ... })
        const data = await apiClients.graphClient.post(`/sphere/${sphereId}/points/status/recalculate`, {
          invalidateCache: true
        });
        console.log('[StatusPopover] Recalculate response:', data);

        // Update local state with the refreshed status point
        if (data?.statusPoint) {
          setLocalStatus(data.statusPoint);
        }

        // Also notify parent so it can update its state if needed
        if (data && onStatusRefreshed) {
          onStatusRefreshed(data.statusPoint || data);
        }
      } catch (err: any) {
        console.error('Failed to recalculate status:', err);
        // Could add error state here if needed
      } finally {
        setIsRefreshing(false);
      }
    }, [apiClients, sphereId, onStatusRefreshed]);

    // Handle opening factor panel with specific mode
    const handleFactorClick = useCallback((factor: FactorData, mode: PanelMode) => {
      setSelectedFactor(factor);
      setPanelMode(mode);
    }, []);

    // Handle closing factor panel
    const handlePanelClose = useCallback(() => {
      setSelectedFactor(null);
      setPanelMode('history'); // Reset to default
    }, []);

    // Handle check-in submission from panel
    const handleCheckInSubmit = useCallback(async (statusValue: PanelStatusValue, comment?: string) => {
      if (!apiClients?.graphClient || !sphereId || !selectedFactor) return;

      setPanelLoading(true);

      try {
        console.log('[StatusPopover] Submitting check-in:', { sphereId, factorId: selectedFactor.id, statusValue, comment });
        // Note: graphClient.post returns data directly (not { data: ... })
        const data = await apiClients.graphClient.post(`/sphere/${sphereId}/factors/${selectedFactor.id}/checkin`, {
          statusValue,
          comment,
        });
        console.log('[StatusPopover] Check-in response:', data);

        // Close panel after successful submission
        setSelectedFactor(null);

        // The check-in response now includes the recalculated statusPoint
        // Use it directly to update the UI without a second API call
        if (data?.statusPoint?.currentValue) {
          console.log('[StatusPopover] Using statusPoint from check-in response:', data.statusPoint);

          // Merge with existing factorDefinitions since the check-in response doesn't include them
          const updatedStatus: SphereStatusPoint = {
            ...data.statusPoint,
            factorDefinitions: effectiveStatus?.factorDefinitions,
          } as SphereStatusPoint;

          setLocalStatus(updatedStatus);

          // Notify parent if callback provided
          onStatusRefreshed?.(updatedStatus);
        } else {
          // Fallback: Refresh the status if check-in response didn't include statusPoint
          console.log('[StatusPopover] No statusPoint in check-in response, falling back to refresh');
          await handleRefresh();
        }
      } catch (err: any) {
        console.error('Failed to submit check-in:', err);
        // TODO: Show error toast/message
      } finally {
        setPanelLoading(false);
      }
    }, [apiClients, sphereId, selectedFactor, handleRefresh, effectiveStatus, onStatusRefreshed]);

    const handleShow = useCallback(() => {
      console.log('[StatusPopoverMolecule] onShow triggered');
      // Notify context to close other popovers
      popoverContext?.onPopoverOpen(sphereId);
      setIsVisible(true);
      // Reset panel state when popover opens
      setSelectedFactor(null);
      setPanelLoading(false);

      // After render, center the popover in the viewport
      requestAnimationFrame(() => {
        const panel = overlayRef.current?.getElement?.();
        if (panel) {
          const rect = panel.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;

          // Calculate center position
          const centerLeft = (viewportWidth - rect.width) / 2;
          const centerTop = (viewportHeight - rect.height) / 2;

          // Apply centered position
          panel.style.left = `${Math.max(10, centerLeft)}px`;
          panel.style.top = `${Math.max(10, centerTop)}px`;
        }
      });
    }, [popoverContext, sphereId, overlayRef]);

    const handleHide = useCallback(() => {
      console.log('[StatusPopoverMolecule] onHide triggered');
      setIsVisible(false);
      // Reset panel state when popover closes
      setSelectedFactor(null);
      setPanelLoading(false);
    }, []);

    // Close the popover programmatically (e.g., from close button)
    const handleClose = useCallback(() => {
      overlayRef.current?.hide();
    }, [overlayRef]);

    // Handle configure factors - close popover and navigate to SphereEditor Status Factors tab
    const handleConfigureFactors = useCallback(() => {
      overlayRef.current?.hide();
      onConfigureFactors?.(sphereId);
    }, [overlayRef, onConfigureFactors, sphereId]);

    // Only render content when popover is visible - avoids Redux selectors on every Atlas render
    return (
      <OverlayPanel
        ref={overlayRef}
        className={`status-popover ${selectedFactor ? 'status-popover--panel-open' : ''}`}
        onShow={handleShow}
        onHide={handleHide}
        appendTo={document.body}>
        {isVisible && (
          <StatusPopoverContent
            sphereId={sphereId}
            sphereName={sphereName}
            schemaType={schemaType}
            status={effectiveStatus}
            isDeploying={isDeploying}
            deployError={deployError}
            onDeployFactors={handleDeployFactors}
            isRefreshing={isRefreshing}
            onRefresh={handleRefresh}
            onClose={handleClose}
            apiClients={apiClients}
            selectedFactor={selectedFactor}
            panelMode={panelMode}
            panelLoading={panelLoading}
            onFactorClick={handleFactorClick}
            onPanelClose={handlePanelClose}
            onCheckInSubmit={handleCheckInSubmit}
            hasEditPermission={hasEditPermission}
            onConfigureFactors={onConfigureFactors ? handleConfigureFactors : undefined}
            velocityData={velocityData}
          />
        )}
      </OverlayPanel>
    );
  }
);

StatusPopoverMolecule.displayName = 'StatusPopoverMolecule';

export default StatusPopoverMolecule;
